import { useCallback, useMemo } from "react";
import { useMistralNarrativeOverlay } from "@/hooks/useMistralNarrativeOverlay";
import { bulletsToActionQueue } from "@/utils/mistralInsightMappers";
import { parseInsightBullets } from "@/utils/parseInsightBullets";
import {
  BarChart3,
  Building2,
  FileText,
  LineChart as LineChartIcon,
  Plus,
  School,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  buildActionQueue,
  buildEnrolmentExecutiveMarkdown,
  buildExecutiveHeroNarrative,
  buildExecutiveInsights,
  buildManagementProfile,
  buildPrimaryExecutiveAction,
  buildRetentionGaugeStyle,
  buildSizeBars,
  buildStageDonutStyle,
  buildStageRows,
  fmt,
  pct,
} from "@/utils/enrolmentDerived";
import HeroStatusChip from "@/components/HeroStatusChip";
import "./EnrolmentExecutiveTab.css";
import { toTitleCase } from "../utils/toTitleCase";

const INSIGHT_ICONS = {
  green: BarChart3,
  pink: Plus,
  orange: FileText,
};

const ACTION_ICONS = {
  pink: Plus,
  green: LineChartIcon,
  orange: FileText,
};

const ClassChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="enr-class-tooltip">
      <p>{label}</p>
      {payload.map((entry) => (
        <p key={entry.name}>
          {entry.name}:{" "}
          {entry.dataKey === "gpi"
            ? Number(entry.value).toFixed(2)
            : typeof entry.value === "number"
              ? entry.value.toLocaleString("en-IN")
              : entry.value}
        </p>
      ))}
    </div>
  );
};

const formatClassLabel = (name = "") => {
  if (/^PP\d$/i.test(name)) return name.replace(/^PP/i, "PP-");
  return name;
};

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const buildHeroTitle = (overview = {}) => {
  const retention = overview.secondary_hs_retention ?? 0;
  const gpi = overview.gender_parity_index ?? 1;
  if (retention >= 80 && gpi < 0.95) return "Healthy enrolment base with gender gap watch";
  if (retention >= 80) return "Healthy enrolment base with stable retention";
  return "Enrolment scale review with transition focus";
};

export default function EnrolmentExecutiveTab({
  overview = {},
  stageData = [],
  sizeDistribution = [],
  classData = [],
  managementWiseData = [],
  blockData = [],
}) {
  const retention = overview.secondary_hs_retention ?? 0;
  const girlsPct = overview.girls_participation_pct ?? 0;
  const gpi = overview.gender_parity_index ?? 0;
  const avgSchool = Math.round(overview.avg_school_size ?? 0);
  const hasData = Boolean(overview?.total_schools);

  const buildLocalMarkdown = useCallback(
    () => buildEnrolmentExecutiveMarkdown(overview, blockData),
    [overview, blockData]
  );

  const scopeKey = useMemo(
    () => JSON.stringify({ s: overview?.total_schools, g: overview?.grand_total }),
    [overview?.total_schools, overview?.grand_total]
  );

  const { loading: aiLoading, pickFromBucket, pickPrimaryAction, pickHeadline, isMistral, buckets } =
    useMistralNarrativeOverlay({
      kind: "enrolment-executive",
      title: "Enrolment Executive Summary",
      buildLocalMarkdown,
      context: { overview, blockData },
      enabled: hasData,
      scopeKey,
    });

  const gaugeStyle = useMemo(
    () => buildRetentionGaugeStyle(retention),
    [retention]
  );

  const localNarrative = useMemo(() => buildExecutiveHeroNarrative(overview), [overview]);
  const narrative = useMemo(
    () => (aiLoading ? localNarrative : pickFromBucket(localNarrative, "insights", 0)),
    [aiLoading, pickFromBucket, localNarrative]
  );
  const localPrimaryAction = useMemo(
    () => buildPrimaryExecutiveAction(overview, blockData),
    [overview, blockData]
  );
  const primaryAction = useMemo(
    () => (aiLoading ? localPrimaryAction : pickPrimaryAction(localPrimaryAction)),
    [aiLoading, pickPrimaryAction, localPrimaryAction]
  );
  const stageRows = useMemo(
    () => buildStageRows(stageData, overview),
    [stageData, overview]
  );
  const stageDonutStyle = useMemo(() => buildStageDonutStyle(stageRows), [stageRows]);
  const insights = useMemo(
    () => buildExecutiveInsights(overview, stageRows, managementWiseData),
    [overview, stageRows, managementWiseData]
  );
  const sizeBars = useMemo(() => buildSizeBars(sizeDistribution), [sizeDistribution]);
  const classChartData = useMemo(
    () =>
      classData.map((row) => ({
        ...row,
        label: formatClassLabel(row.class_name),
        total: row.total ?? 0,
        gpi: typeof row.gpi === "number" ? row.gpi : null,
      })),
    [classData]
  );
  const management = useMemo(
    () => buildManagementProfile(overview, managementWiseData),
    [overview, managementWiseData]
  );
  const localActionQueue = useMemo(() => buildActionQueue(overview), [overview]);
  const actionQueue = useMemo(() => {
    if (aiLoading) return localActionQueue;
    const aiActions = bulletsToActionQueue(
      parseInsightBullets(buckets.actions).length
        ? parseInsightBullets(buckets.actions)
        : parseInsightBullets(buckets.recs),
      localActionQueue
    );
    return aiActions?.length
      ? aiActions.map((action, i) => ({
          ...localActionQueue[i],
          ...action,
          priority: localActionQueue[i]?.priority || `Priority ${i + 1}`,
        }))
      : localActionQueue;
  }, [aiLoading, buckets.actions, buckets.recs, localActionQueue]);

  const localHeroTitle = useMemo(() => buildHeroTitle(overview), [overview]);
  const heroTitle = useMemo(
    () => (aiLoading ? localHeroTitle : pickHeadline(localHeroTitle)),
    [aiLoading, pickHeadline, localHeroTitle]
  );

  const managementRingStyle = useMemo(
    () => ({
      background: `radial-gradient(circle at center, #fff 0 54%, transparent 55%), conic-gradient(var(--enr-secondary) 0 ${management.sharePct}%, rgba(255,138,31,.14) ${management.sharePct}% 100%)`,
    }),
    [management.sharePct]
  );

  const retentionChip =
    retention >= 80 ? "Stable retention" : retention >= 70 ? "Retention watch" : "Retention alert";
  const girlsChipClass = girlsPct < 48 ? "orange" : "blue";

  return (
    <div className="enr-exec-tab">
      <section className="enr-exec-hero" id="enr-exec-hero">
        <div className="enr-exec-hero-layout">
          <div>
            <div className="enr-score-panel">
              <div
                className="enr-gauge"
                style={gaugeStyle}
                aria-label={`Secondary and Higher Secondary retention ${pct(retention)}`}
              >
                <div>
                  <strong>{pct(retention)}</strong>
                  <span>Retention</span>
                </div>
              </div>
              <div className="enr-hero-copy">
                <h2>{toTitleCase(heroTitle)}</h2>
                <p>{narrative}</p>
                <div className="enr-status-row">
                  <HeroStatusChip
                    chipClass="enr-status-chip"
                    onClick={retention < 80 ? () => scrollToId("enr-stage") : undefined}
                    ariaLabel={retention < 80 ? `${retentionChip} — view stage-wise retention` : undefined}
                  >
                    {retentionChip}
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="enr-status-chip"
                    variant={girlsChipClass}
                    onClick={girlsPct < 48 ? () => scrollToId("enr-classwise") : undefined}
                    ariaLabel={
                      girlsPct < 48 ? `${pct(girlsPct)} girls — view class-wise gender breakdown` : undefined
                    }
                  >
                    {pct(girlsPct)} girls
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="enr-status-chip"
                    variant="orange"
                    onClick={gpi < 0.95 || gpi > 1.05 ? () => scrollToId("enr-classwise") : undefined}
                    ariaLabel={
                      gpi < 0.95 || gpi > 1.05
                        ? `GPI watch ${gpi} — view class-wise gender parity`
                        : undefined
                    }
                  >
                    GPI watch: {gpi}
                  </HeroStatusChip>
                </div>
              </div>
            </div>
            <div className="enr-summary-note">
              <div className="enr-icon-box purple">
                <Zap size={22} />
              </div>
              <div>
                <b>Primary executive action</b>
                <span>{primaryAction}</span>
              </div>
            </div>
          </div>

          <div className="enr-metric-grid" aria-label="Enrolment Executive KPIs">
            <article className="enr-metric-card">
              <span className="enr-mini-icon">
                <Building2 size={18} />
              </span>
              <small>Total Schools</small>
              <strong>{fmt(overview.total_schools)}</strong>
              <span className="enr-metric-foot muted">Across selected district</span>
            </article>
            <article className="enr-metric-card">
              <span className="enr-mini-icon">
                <Users size={18} />
              </span>
              <small>Total Enrolment</small>
              <strong>{fmt(overview.grand_total)}</strong>
              <span className="enr-metric-foot success">Avg {avgSchool} students / school</span>
            </article>
            <article className="enr-metric-card">
              <span className="enr-mini-icon">
                <Plus size={18} />
              </span>
              <small>Girls Share</small>
              <strong>{pct(girlsPct)}</strong>
              <span className="enr-metric-foot warning">Needs parity push</span>
            </article>
            <article className="enr-metric-card">
              <span className="enr-mini-icon">
                <Plus size={18} />
              </span>
              <small>Gender Parity Index</small>
              <strong>{gpi}</strong>
              <span className="enr-metric-foot muted">Girls / Boys</span>
            </article>
            <article className="enr-metric-card">
              <span className="enr-mini-icon">
                <School size={18} />
              </span>
              <small>Avg School Size</small>
              <strong>{avgSchool}</strong>
              <span className="enr-metric-foot muted">Students per school</span>
            </article>
            <article className="enr-metric-card">
              <span className="enr-mini-icon">
                <TrendingUp size={18} />
              </span>
              <small>Sec + HS Retention</small>
              <strong>{pct(retention)}</strong>
              <span className="enr-metric-foot success">Class 10 → 11</span>
            </article>
          </div>
        </div>
      </section>

      <div className="enr-section-title" id="enr-insights">
        <div>
          <h2>Executive Insights</h2>
          <p>
            Important findings are pulled above the charts, so users know what to act on before studying
            detailed visuals.
          </p>
        </div>
        <button type="button" className="enr-section-action" onClick={() => scrollToId("enr-classwise")}>
          View class trend →
        </button>
      </div>

      <section className="enr-insight-grid">
        {insights.map((item) => {
          const Icon = INSIGHT_ICONS[item.iconClass] || BarChart3;
          return (
            <article key={item.id} className="enr-insight-card">
              <div className={`enr-icon-box ${item.iconClass}`}>
                <Icon size={22} />
              </div>
              <h3>{toTitleCase(item.title)}</h3>
              <p>{item.body}</p>
            </article>
          );
        })}
      </section>

      <div className="enr-section-title" id="enr-stage">
        <div>
          <h2>Stage Distribution</h2>
          <p>
            The previous donut is retained as a visual cue, but paired with readable bars so users can
            compare stages without decoding a small legend.
          </p>
        </div>
        <button type="button" className="enr-section-action" onClick={() => scrollToId("enr-size")}>
          School-size mix →
        </button>
      </div>

      <section className="enr-analytics-grid">
        <article className="enr-panel">
          <div className="enr-panel-head">
            <div>
              <h3>Education Stage Distribution</h3>
              <p>NEP-aligned stage grouping by share of total enrolment.</p>
            </div>
            <span className="enr-pill">Share %</span>
          </div>
          <div className="enr-stage-list" aria-label="Education stage distribution bar chart">
            {stageRows.map((row) => (
              <div key={row.key} className="enr-stage-row">
                <div className="enr-stage-label">
                  {row.label}
                  {row.sub ? (
                    <>
                      <br />
                      {row.sub}
                    </>
                  ) : null}
                </div>
                <div className="enr-stage-track">
                  <div
                    className={`enr-stage-bar ${row.barClass}`.trim()}
                    style={{ width: `${row.percentage}%` }}
                  />
                </div>
                <div className="enr-stage-value">{pct(row.percentage)}</div>
              </div>
            ))}
          </div>
        </article>

        <article className="enr-panel">
          <div className="enr-panel-head">
            <div>
              <h3>Stage Mix Snapshot</h3>
              <p>High-level shape of enrolment across school stages.</p>
            </div>
            <span className="enr-pill">NEP stages</span>
          </div>
          <div className="enr-donut-layout">
            <div className="enr-donut" style={stageDonutStyle}>
              <div>
                <b>100%</b>
                <small>Total Mix</small>
              </div>
            </div>
            <div className="enr-legend">
              {stageRows.map((row) => (
                <div key={row.key} className="enr-legend-row">
                  <span>
                    <i className={`enr-dot ${row.barClass}`.trim()} />
                    {row.label}
                  </span>
                  <b>{pct(row.percentage)}</b>
                </div>
              ))}
            </div>
          </div>
        </article>
      </section>

      <div className="enr-section-title" id="enr-size">
        <div>
          <h2>School-Size and Management Profile</h2>
          <p>
            Chart areas are separated into dedicated panels with larger canvas, fewer gridlines and clearer
            executive interpretation.
          </p>
        </div>
        <button type="button" className="enr-section-action" onClick={() => scrollToId("enr-classwise")}>
          Class trend →
        </button>
      </div>

      <section className="enr-wide-grid">
        <article className="enr-panel">
          <div className="enr-panel-head">
            <div>
              <h3>School Size Distribution</h3>
              <p>Number of schools by student count band.</p>
            </div>
            <span className="enr-pill">Schools</span>
          </div>
          <div className="enr-size-bars" aria-label="School size distribution bar chart">
            {sizeBars.map((item) => (
              <div key={item.range} className="enr-size-col">
                <span className="enr-size-value">{fmt(item.count)}</span>
                <div
                  className={`enr-size-bar ${item.tone}`.trim()}
                  style={{ height: `${item.barHeight}px` }}
                />
                <span>{item.range}</span>
              </div>
            ))}
          </div>
        </article>

        <article className="enr-panel enr-management-card">
          <div className="enr-panel-head">
            <div>
              <h3>Enrolment by Management Type</h3>
              <p>Use as an executive data-quality checkpoint.</p>
            </div>
            <span className="enr-pill">
              {management.otherOnly ? "Validation required" : "Management mix"}
            </span>
          </div>
          <div className="enr-management-hero">
            <div className="enr-ring" style={managementRingStyle}>
              <div>
                <strong>{management.sharePct}%</strong>
                <small>{management.managementLabel}</small>
              </div>
            </div>
            <div>
              <h4>{management.headline}</h4>
              <p>{management.description}</p>
            </div>
          </div>
          <div className="enr-data-quality">
            <div className="enr-dq-tile">
              <small>Schools classified</small>
              <b>{management.schoolsClassified}</b>
            </div>
            <div className="enr-dq-tile">
              <small>Total enrolment</small>
              <b>{management.totalEnrolment}</b>
            </div>
            <div className="enr-dq-tile">
              <small>Avg / school</small>
              <b>{management.avgSchool}</b>
            </div>
          </div>
        </article>
      </section>

      <div className="enr-section-title" id="enr-classwise">
        <div>
          <h2>Class-Wise Enrolment Trend</h2>
          <p>
            The final chart is given full-width treatment so the Class 1–12 journey can be scanned without
            visual compression.
          </p>
        </div>
        <button type="button" className="enr-section-action" onClick={() => scrollToId("enr-exec-hero")}>
          Back to top ↑
        </button>
      </div>

      <section className="enr-panel enr-class-panel">
        <div className="enr-panel-head">
          <div>
            <h3>Class-Wise Enrolment From PP3 to Class 12</h3>
            <p>Bars show enrolment volume; line shows gender parity index.</p>
          </div>
          <span className="enr-pill">Class trend</span>
        </div>
        <div className="enr-class-chart" aria-label="Class-wise enrolment chart">
          {classChartData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={290}>
                <ComposedChart
                  data={classChartData}
                  margin={{ top: 24, right: 12, left: 12, bottom: 0 }}
                  barCategoryGap="6%"
                >
                  <defs>
                    <linearGradient id="enrClassBarGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2563eb" stopOpacity={0.55} />
                      <stop offset="100%" stopColor="#2563eb" stopOpacity={1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(37, 99, 235, 0.08)" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 10, fontWeight: 950, fill: "#58657d" }}
                    interval={0}
                    axisLine={{ stroke: "rgba(37, 99, 235, 0.14)" }}
                    tickLine={false}
                    dy={8}
                  />
                  <YAxis yAxisId="enrolment" hide domain={[0, "auto"]} />
                  <YAxis yAxisId="gpi" hide domain={[0.75, 1.05]} />
                  <Tooltip content={<ClassChartTooltip />} />
                  <Bar
                    yAxisId="enrolment"
                    dataKey="total"
                    name="Total Enrolment"
                    fill="url(#enrClassBarGrad)"
                    radius={[12, 12, 4, 4]}
                    maxBarSize={80}
                  />
                  <Line
                    yAxisId="gpi"
                    type="monotone"
                    dataKey="gpi"
                    name="Gender Parity Index"
                    stroke="#14b8a6"
                    strokeWidth={3}
                    dot={{ r: 4, fill: "#14b8a6", stroke: "#fff", strokeWidth: 1.5 }}
                    activeDot={{ r: 6 }}
                    connectNulls
                  />
                </ComposedChart>
              </ResponsiveContainer>
              <div className="enr-class-legend">
                <span>
                  <i className="enr-line-key" />
                  Gender Parity Index
                </span>
                <span>
                  <i className="enr-bar-key" />
                  Total Enrolment
                </span>
              </div>
            </>
          ) : null}
        </div>
      </section>

      <div className="enr-section-title" id="enr-actions">
        <div>
          <h2>Recommended Action Queue</h2>
          <p>
            These three cards turn the executive overview into an action-oriented dashboard rather than a
            passive reporting page.
          </p>
        </div>
      </div>

      <section className="enr-action-grid">
        {actionQueue.map((item) => {
          const Icon = ACTION_ICONS[item.iconClass] || FileText;
          return (
            <article key={item.priority} className="enr-action-card">
              <span className="enr-priority">{item.priority}</span>
              <div className={`enr-icon-box ${item.iconClass}`}>
                <Icon size={22} />
              </div>
              <h3>{toTitleCase(item.title)}</h3>
              <p>{item.body}</p>
            </article>
          );
        })}
      </section>
    </div>
  );
}
